import { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { QueryKey, QueryFunction, UseQueryOptions, UseQueryResult } from 'react-query/types';
import { debounce } from '../utils/debounce';

interface UseDebouncedQueryOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey = QueryKey>
	extends UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> {
	delay: number;
}

const useDebouncedQuery = <
	TQueryFnData = unknown,
	TError = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey
>(
	queryKey: TQueryKey,
	queryFn: QueryFunction<TQueryFnData, TQueryKey>,
	options: UseDebouncedQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> => {
	const [memoData, setMemoData] = useState<[TQueryKey, QueryFunction<TQueryFnData, TQueryKey>]>([queryKey, queryFn]);

	const setMemoDataDebounced = useMemo(() => debounce(setMemoData, options.delay), []);

	useEffect(() => {
		setMemoDataDebounced([queryKey, queryFn, options]);
	}, [queryKey, queryFn, options]);

	return useQuery(...memoData, options);
};

export default useDebouncedQuery;
